import { useT } from '../i18n/index.ts';
import { firewallDiffIsEmpty, type FirewallRuleDiffEntry } from '../lib/firewallRuleDiff.ts';
import { Chip } from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';

/**
 * Rule-level diff between two versions (#1760).
 *
 * `moved` is a first-class change kind, not a cosmetic one: evaluation is first
 * match wins, so a rule whose text is byte-identical can change every verdict
 * below it just by moving. An operator comparing "what is running" with "what I
 * am about to activate" has to see that.
 */

const KIND_TONE = {
  added: 'ok',
  removed: 'bad',
  changed: 'warn',
  moved: 'signal',
  unchanged: 'neutral',
} as const satisfies Record<FirewallRuleDiffEntry['kind'], ChipTone>;

function positionLabel(entry: FirewallRuleDiffEntry): string {
  switch (entry.kind) {
    case 'added':
      return `#${String(entry.toIndex + 1)}`;
    case 'removed':
      return `#${String(entry.fromIndex + 1)}`;
    case 'unchanged':
      return `#${String(entry.index + 1)}`;
    default:
      return entry.fromIndex === entry.toIndex
        ? `#${String(entry.toIndex + 1)}`
        : `#${String(entry.fromIndex + 1)} → #${String(entry.toIndex + 1)}`;
  }
}

export function FirewallVersionDiff({
  entries,
  testId,
}: {
  /** `null` when one side is not a readable rule array — see `diffFirewallRules`. */
  readonly entries: readonly FirewallRuleDiffEntry[] | null;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  if (entries === null) {
    return (
      <p className="m-0 rounded-lg bg-well p-3 text-label text-warn" data-testid={`${testId}-unreadable`}>
        {t('firewall_rulesets.diff.unreadable')}
      </p>
    );
  }
  if (firewallDiffIsEmpty(entries)) {
    return (
      <p className="m-0 rounded-lg bg-well p-3 text-label text-paper-mid" data-testid={`${testId}-identical`}>
        {t('firewall_rulesets.diff.identical')}
      </p>
    );
  }
  const changed = entries.filter((entry) => entry.kind !== 'unchanged');
  return (
    <ul className="m-0 flex list-none flex-col gap-1.5 p-0" data-testid={testId}>
      {changed.map((entry) => (
        <li
          key={`${entry.kind}:${entry.id}`}
          className="flex flex-wrap items-center gap-2 rounded bg-well px-2 py-1.5"
        >
          <Chip tone={KIND_TONE[entry.kind]} lamp={false}>
            {t(`firewall_rulesets.diff.kind.${entry.kind}`)}
          </Chip>
          <span className="font-mono text-label text-paper">{entry.id}</span>
          <span className="font-mono text-micro text-paper-low">{positionLabel(entry)}</span>
          {entry.kind === 'changed' ? (
            <span className="font-mono text-micro text-paper-mid">{entry.fields.join(' · ')}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
